import { Checkbox } from '@ets/design-system';

import EventsManager from 'Editor/services/EventsManager';
import { ELEMENTS } from 'Editor/services/consts';
import EditorManager from 'Editor/services/EditorManager';
import styles from './PermissionViewElement.module.scss';
import { EditorDOMUtils } from 'Editor/services/_Common/DOM';
import { BaseBlockElement } from '../../../ViewBuilders';
import { Root } from 'react-dom/client';
import ReactUtils from '_common/utils/ReactUtils';
import SuiteAvatar from '_common/suite/components/SuiteAvatar/SuiteAvatar';

type PermissionUserListType = {
  usersPermissionList: UserId[];
};

const PermissionUserListComponent = ({ usersPermissionList }: PermissionUserListType) => {
  return (
    <>
      {usersPermissionList.length > 0 &&
        usersPermissionList.map((user) => (
          <span className={styles.userElement} key={user}>
            <SuiteAvatar size="s" userId={user} showTooltip />
          </span>
        ))}
    </>
  );
};

export class PermissionViewElement extends BaseBlockElement {
  //Selection a record of selected elements, but data is duplicated in PermissionsController
  static SELECTION: Editor.Decorator.Selection = {};
  static SELECTING: boolean = false;

  static handleMouseDown() {
    PermissionViewElement.SELECTING = false;

    EventsManager.getInstance().on('DOCUMENT_MOUSE_UP', PermissionViewElement.handleMouseUp);
  }

  static handleMouseUp(e: MouseEvent) {
    let element = e.target as HTMLElement;
    let target = e.target as HTMLElement;

    if (element.tagName !== ELEMENTS.PermissionViewElement.TAG) {
      element = EditorDOMUtils.closest(
        element,
        ELEMENTS.PermissionViewElement.TAG,
      ) as PermissionViewElement;
    }

    if (!PermissionViewElement.SELECTING) {
      if (element && element instanceof PermissionViewElement) {
        if (
          target &&
          document
            .getElementById(`PermissionIconWrapper-${element.getAttribute('enclosed_element')}`)
            ?.contains(target)
        ) {
          element.toggleSelected();
        } else {
          EditorManager.getInstance().deselectAllForPermissions();
          element.selected = true;
        }
      }
    }

    // publish select bulk action
    const keys = Object.keys(PermissionViewElement.SELECTION);
    if (keys.length > 0) {
      const selectedElements = [];
      for (let i = 0; i < keys.length; i += 1) {
        if (PermissionViewElement.SELECTION[keys[i]]) {
          selectedElements.push(keys[i]);
        }
      }
      EditorManager.getInstance().selectNodesForPermissions(selectedElements);
    }

    PermissionViewElement.SELECTING = false;

    EventsManager.getInstance().removeListener(
      'DOCUMENT_MOUSE_UP',
      PermissionViewElement.handleMouseUp,
    );
  }

  static get observedAttributes() {
    return ['selected'];
  }

  private rootReactIconContainer!: Root;
  private rootReactUsersContainer!: Root;

  private container!: HTMLDivElement;
  private iconWrapper!: HTMLDivElement;
  private contentWrapper!: HTMLDivElement;
  private users!: HTMLSpanElement;
  private content!: HTMLSpanElement;

  private nodeContent: HTMLElement | null;
  private usersContent: { users: UserId[] } = { users: [] };

  constructor() {
    super();

    this.nodeContent = null;
    this.usersContent = { users: [] };

    this.handleSelectedAttributeChange = this.handleSelectedAttributeChange.bind(this);
  }

  attributeChangedCallback(name: string, oldValue: any, newValue: any) {
    if (this.isConnected) {
      if (name === 'selected' && oldValue !== newValue) {
        this.handleSelectedAttributeChange(newValue);
      } else {
        super.attributeChangedCallback(name, oldValue, newValue);
      }
    }
  }

  handleSelectedAttributeChange(newValue: any) {
    const enclosedNodeId: string = this.getAttribute('enclosed_element')!;
    if (newValue) {
      this.setIcon();
      PermissionViewElement.SELECTION[enclosedNodeId] = true;
    } else {
      this.setIcon();
      PermissionViewElement.SELECTION[enclosedNodeId] = false;
    }
  }

  get selected() {
    return this.hasAttribute('selected');
  }

  set selected(val) {
    if (val) {
      this.setAttribute('selected', 'true');
    } else {
      this.removeAttribute('selected');
    }
  }

  private applyStyles() {
    const selected = this.selected ? 'true' : 'false';

    this.content.setAttribute('data-selected', selected);
    this.users.setAttribute('data-selected', selected);
  }

  setIcon() {
    this.applyStyles();
    ReactUtils.renderReactComponentWithoutRedux(
      <Checkbox
        checked={this.selected}
        size="s"
        onChange={() => {}}
        testId={`checkbox-column-${this.getAttribute('enclosed_element')}`}
      />,
      this.rootReactIconContainer,
    );
  }

  deselect() {
    this.removeAttribute('selected');
  }

  toggleSelected() {
    this.selected = !this.selected;
  }

  setContent(child: HTMLElement) {
    this.nodeContent = child;
  }

  setUsers(users: UserId[]) {
    this.usersContent = {
      users,
    };
    this.updateApproved();
  }

  updateApproved() {
    ReactUtils.renderReactComponent(
      <PermissionUserListComponent usersPermissionList={this.usersContent.users} />,
      this.rootReactUsersContainer,
    );
  }

  connectedCallback() {
    super.connectedCallback();

    // clean childreen
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }

    this.addEventListener('mousedown', PermissionViewElement.handleMouseDown);

    this.container = document.createElement('div');
    this.container.setAttribute('class', styles.container);
    this.iconWrapper = document.createElement('div');
    this.iconWrapper.id = `PermissionIconWrapper-${this.getAttribute('enclosed_element')}`;
    this.iconWrapper.setAttribute('class', styles.iconWrapper);

    this.contentWrapper = document.createElement('div');
    this.contentWrapper.setAttribute('class', styles.contentWrapper);

    this.content = document.createElement('span');
    this.content.setAttribute('class', styles.content);
    this.users = document.createElement('span');
    this.users.setAttribute('class', styles.users);

    this.appendChild(this.container);
    this.container.appendChild(this.iconWrapper);
    this.container.appendChild(this.contentWrapper);
    this.contentWrapper.appendChild(this.content);
    this.contentWrapper.appendChild(this.users);

    if (this.nodeContent) {
      this.content.appendChild(this.nodeContent);
    }

    if (!this.rootReactIconContainer) {
      this.rootReactIconContainer = ReactUtils.createReactRoot(this.iconWrapper);
    }
    if (!this.rootReactUsersContainer) {
      this.rootReactUsersContainer = ReactUtils.createReactRoot(this.users);
    }

    this.setIcon();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    ReactUtils.unmountReactComponent(this.rootReactIconContainer);
    ReactUtils.unmountReactComponent(this.rootReactUsersContainer);

    this.removeEventListener('mousedown', PermissionViewElement.handleMouseDown);
  }
}

if (!window.customElements.get(ELEMENTS.PermissionViewElement.IDENTIFIER)) {
  window.customElements.define(ELEMENTS.PermissionViewElement.IDENTIFIER, PermissionViewElement);
}
